import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Stack,
  Divider,
  CircularProgress,
  Alert,
} from "@mui/material";

import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import axios from "axios";

import { SET_REPORT_DATA } from "../../store/actions";
import { safeFetch } from "./Safefetch";

/**
 * API base (adjust for EC2 later)
 */
//const API_BASE = "http://127.0.0.1:8000";
const API_BASE = "http://ec2-43-205-136-189.ap-south-1.compute.amazonaws.com"

const LL_AUTH_HEADERS = () => ({
    Authorization: `Bearer ${localStorage.getItem("labour_law_token") || ""}`,
});

/**
 * Card grouping for UI sections
 */
const SECTION_ORDER = [
  { id: "repo", title: "Repository" },
  { id: "templates", title: "Templates" },
  { id: "users", title: "Users" },
  { id: "search", title: "Search Analytics" },
  { id: "ai", title: "AI Law Bot" },
  { id: "notifications", title: "Notifications" },
];

/**
 * Map backend card keys to UI section
 * (Backend decides cards; frontend decides grouping)
 */
const CARD_SECTION = {
  // Repository
  total_ll_nodes: "repo",
  active_ll_nodes: "repo",
  state_coverage: "repo",
  domain_category_coverage: "repo",
  // Templates
  total_templates: "templates",
  template_coverage: "templates",
  // Users
  total_users: "users",
  paid_users: "users",
  // Search
  searches_30d: "search",
  avg_results_per_search: "search",
  search_click_rate: "search",
  top_search_terms: "search",
  // AI
  ai_chat_queries_30d: "ai",
  ai_chat_users_30d: "ai",
  // Notifications
  notif_sent_30d: "notifications",
};

const todayLabel = () => new Date().toLocaleDateString("en-IN");

const apiGet = async (path) => {
  const res = await safeFetch(`${API_BASE}${path}`, {
    method: "GET",
    headers: { "Content-Type": "application/json", ...LL_AUTH_HEADERS() },
    credentials: "include",
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`API ${res.status} ${path}${text ? ` - ${text.slice(0, 200)}` : ""}`);
  }
  return res.json();
};

export default function StatisticsPane() {
  const history = useHistory();
  const dispatch = useDispatch();

  const [days, setDays] = useState(30);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  

  /**
   * Secure Power BI open:
   * - frontend sends report_key only
   * - backend returns embedToken + embedUrl
   * - dispatch to Redux
   * - navigate to PowerBiReportPage
   */
  const openPowerBI = useCallback(
    async (card) => {
      const reportKey = card?.report_key;
      if (!reportKey) {
        setError("No Power BI mapping found for this card.");
        return;
      }

      try {
        setError("");

        const tokenRes = await axios.post(
          `${API_BASE}/analytics/powerbi/embed-token/`,
          { report_key: reportKey },
          { withCredentials: true, headers: { "Content-Type": "application/json",  ...LL_AUTH_HEADERS() } }
        );

        const { embedToken, embedUrl, reportId, datasetId, groupId } = tokenRes.data || {};

        if (!embedToken || !embedUrl) {
          throw new Error("Power BI token response missing embedToken/embedUrl.");
        }

        dispatch({
          type: SET_REPORT_DATA,
          payload: {
            title: card.title || "Power BI Report",
            isSecure: true,
            embedUrl,
            accessToken: embedToken,
            reportId,
            datasetId,
            groupId,
          },
        });

        history.push("/power-bi-report-page");
      } catch (e) {
        setError(e?.response?.data?.error || e?.message || "Failed to open Power BI");
      }
    },
    [dispatch, history]
  );

  const clickableCardSx = {
    cursor: "pointer",
    transition: "transform 140ms ease, box-shadow 140ms ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 8px 20px rgba(0,0,0,0.10)",
    },
  };

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError("");

    (async () => {
      try {
        const data = await apiGet(`/analytics/overview/?days=${days}`,  { headers: LL_AUTH_HEADERS() });
        if (!mounted) return;
        setCards(Array.isArray(data?.cards) ? data.cards : []);
      } catch (e) {
        if (!mounted) return;
        setError(e?.message || "Failed to load analytics");
        setCards([]);
      } finally {
        mounted && setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [days]);

  const grouped = useMemo(() => {
    const bySection = {};
    for (const s of SECTION_ORDER) bySection[s.id] = [];
    for (const c of cards) {
      const sec = CARD_SECTION[c.key] || "repo";
      if (!bySection[sec]) bySection[sec] = [];
      bySection[sec].push(c);
    }
    return bySection;
  }, [cards]);

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 900 }}>
            Admin Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Today: {todayLabel()} • Window: last {days} days
          </Typography>
        </Box>

        {loading ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Loading…
            </Typography>
          </Stack>
        ) : null}
      </Stack>

      {!!error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2}>
        {SECTION_ORDER.map((sec) => {
          const secCards = grouped?.[sec.id] || [];
          if (!secCards.length) return null;

          return (
            <Grid item xs={12} key={sec.id}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Stack spacing={1}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                      {sec.title}
                    </Typography>
                    <Divider />
                    <Grid container spacing={2}>
                      {secCards.map((c, idx) => (
                        <Grid item xs={12} sm={6} md={3} key={`${c.key}-${idx}`}>
                          <Card
                            sx={{ height: "100%", borderRadius: 2, ...clickableCardSx }}
                            onClick={() => openPowerBI(c)}
                          >
                            <CardContent>
                              <Typography variant="overline" color="text.secondary">
                                {c.title}
                              </Typography>
                              <Typography variant="h6" sx={{ mt: 0.5, fontWeight: 900 }}>
                                {c.value ?? "—"}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                                {c.sub || "Click to open Power BI"}
                              </Typography>
                              <Typography variant="caption" color="primary" sx={{ display: "block", mt: 0.5, fontWeight: 700 }}>
                                Click → Power BI details
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}
